import { DispatchRequest, RequestAction, resetRequests } from '@redux-requests/core';
import { gql } from '@redux-requests/graphql';
import { AuthConstants } from './constants';
import authService from '../authService';
import { getAccount } from 'modules/layout/store/actions';

export interface IDeveloperProfilePayload {
  companyName: string;
  firstName: string;
  lastName: string;
  purpose: string;
  website: string;
}

export const registerDeveloperAccount = (
  email: string,
  registrationData: IDeveloperProfilePayload
): RequestAction => ({
  type: AuthConstants.AUTH_REGISTER,
  request: {
    query: gql`
      mutation($email: String!, $registrationData: DeveloperProfilePayload!) {
        registerDeveloperAccountWithEmailAndPassword(
          email: $email
          registrationData: $registrationData
        )
      }
    `,
    variables: { email, registrationData },
  },
});

export interface ILoginDeveloperAccount {
  email: string;
  password: string;
  locale?: string;
  longSession?: string;
}

export const loginDeveloperAccount = ({
  email,
  password,
  locale,
  longSession,
}: ILoginDeveloperAccount): RequestAction => ({
  type: AuthConstants.AUTH_LOGIN,
  request: {
    query: gql`
      mutation($email: String!, $password: String!, $locale: Locale, $longSession: Boolean) {
        loginDeveloperAccountWithEmailAndPassword(
          email: $email
          password: $password
          locale: $locale
          longSession: $longSession
        ) {
          authorization
          refreshToken
          sessionDuration
          account {
            id
          }
        }
      }
    `,
    variables: { email, password, locale, longSession },
  },
  meta: {
    onSuccess(response: any) {
      const {
        data: {
          loginDeveloperAccountWithEmailAndPassword: {
            authorization,
            refreshToken,
            sessionDuration,
            account,
          },
        },
      } = response;

      authService.setAccessToken(authorization);
      authService.setRefreshToken(refreshToken);
      authService.setExpiredIn(sessionDuration);

      return { data: { account } };
    },
    runOnRequest: false,
  },
});

export const refreshTokenAction = (refreshToken: string): any => ({
  type: AuthConstants.REFRESH_TOKEN,
  request: {
    query: gql`
      query($refreshToken: ID!) {
        refreshTokens(refreshToken: $refreshToken) {
          authorization
          refreshToken
          sessionDuration
        }
      }
    `,
    variables: { refreshToken },
  },
  meta: {
    silent: true, // don't need to store it in reducer
    runOnError: false, // don't need to refresh token during refreshing token
  },
});

export const sendPasswordResetCode = (email: string): RequestAction => ({
  type: AuthConstants.RESET_PASSWORD,
  request: {
    query: gql`
      mutation($email: String!) {
        sendPasswordResetCode(email: $email)
      }
    `,
    variables: { email },
  },
});

export const initAuthAction = (refreshToken: string): any => ({
  type: AuthConstants.AUTH_LOGIN,
  request: {
    query: gql`
      query($refreshToken: ID!) {
        refreshTokens(refreshToken: $refreshToken) {
          authorization
          refreshToken
          sessionDuration
          account {
            id
          }
        }
      }
    `,
    variables: { refreshToken },
  },
  meta: {
    onSuccess: (response: any) => {
      const {
        data: {
          refreshTokens: { authorization, refreshToken, sessionDuration, account },
        },
      } = response;

      authService.setAccessToken(authorization);
      authService.setRefreshToken(refreshToken);
      authService.setExpiredIn(sessionDuration);

      return { data: { account } };
    },
    onError: (error: any) => {
      authService.clearAuthTokens();
      throw error;
    },
    asMutation: true,
    runOnRequest: false, // to avoid infinity loop in onRequest interceptor
  },
});

export const invalidateSession = (): RequestAction => ({
  type: AuthConstants.AUTH_LOGOUT,
  request: {
    query: gql`
      mutation($token: ID) {
        invalidateSession(jwtId: $token)
      }
    `,
    variables: {},
  },
});

export const logoutAction = () => async (dispatch: any) => {
  const token = authService.getAccessToken();
  if (token) {
    await dispatch(invalidateSession());
  }
  authService.clearAuthTokens();
  dispatch(resetRequests());
};

export const loginAndGetAccount = (values: ILoginDeveloperAccount) => async (
  dispatch: DispatchRequest
) => {
  const { data, error } = await dispatch(loginDeveloperAccount(values));

  if (error) {
    return { error };
  }

  await dispatch(getAccount());

  return { data };
};
